import React, { useEffect, useState, useMemo } from 'react';
import { Users, ChevronDown, ChevronUp, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '../store/AuthContext';
import { getUserGroups } from '../services/groups';
import { getGroupExpenses } from '../services/expenses';
import { getUserTabs } from '../services/tabs';
import { getMultipleUserProfiles } from '../services/users';
import { computeBalances, type FriendBalance } from '../services/balances';
import type { Expense, UserProfile } from '../types';
import { cn } from '../utils/cn';
import { formatCurrency } from '../utils/currencyFormatting';
import { RequireAuth } from '../components/RequireAuth';
import { LoadingSpinner } from '../components/LoadingSpinner';

export const FriendsPage: React.FC = () => {
    return (
        <RequireAuth>
            <FriendsPageContent />
        </RequireAuth>
    );
};

const FriendsPageContent: React.FC = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [balances, setBalances] = useState<FriendBalance[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    useEffect(() => {
        if (!user) return;

        const loadBalances = async () => {
            try {
                setLoading(true);

                // Fetch all groups + tabs in parallel
                const [groups, tabs] = await Promise.all([
                    getUserGroups(user.uid),
                    getUserTabs(user.uid),
                ]);

                // Fetch expenses for all groups in parallel
                const expenseResults = await Promise.all(
                    groups.map(g => getGroupExpenses(g.id))
                );
                const expensesByGroup: Record<string, Expense[]> = {};
                groups.forEach((g, i) => {
                    expensesByGroup[g.id] = expenseResults[i] ?? [];
                });

                // Collect all unique UIDs we need profiles for
                const uidSet = new Set<string>();
                for (const group of groups) {
                    group.members.forEach(uid => uidSet.add(uid));
                }
                for (const tab of tabs) {
                    tab.members.forEach(uid => uidSet.add(uid));
                    uidSet.add(tab.ownerId);
                    for (const item of tab.items) {
                        item.claimedBy.forEach(uid => uidSet.add(uid));
                    }
                }
                uidSet.delete(user.uid);

                // Fetch profiles
                const profilesList = await getMultipleUserProfiles(Array.from(uidSet));
                const profilesMap = new Map<string, UserProfile>();
                profilesList.forEach(p => profilesMap.set(p.uid, p));

                const result = computeBalances(user.uid, groups, expensesByGroup, tabs, profilesMap);
                setBalances(result);
            } catch (err) {
                console.error('Failed to load balances:', err);
            } finally {
                setLoading(false);
            }
        };

        void loadBalances();
    }, [user]);

    // Summary stats
    const { totalOwed, totalOwe } = useMemo(() => {
        let owed = 0; // others owe me
        let owe = 0;  // I owe others
        for (const fb of balances) {
            for (const amount of Object.values(fb.netByCurrency)) {
                if (amount > 0) owed += amount;
                else owe += Math.abs(amount);
            }
        }
        return { totalOwed: owed, totalOwe: owe };
    }, [balances]);


    return (
        <div className="h-full flex flex-col">
            {/* Sticky Header */}
            <div className="flex-none px-4 py-3 bg-bg-header border-b border-border-default">
                <h1 className="text-2xl font-black text-text-heading">Friends</h1>
                <p className="text-xs text-text-muted mt-0.5">Outstanding balances across all groups &amp; tabs</p>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">

                {loading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-3 py-12">
                        <LoadingSpinner size="lg" />
                        <p className="text-sm font-medium text-text-muted">Calculating balances…</p>
                    </div>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 p-4 text-white shadow-lg shadow-green-500/20">
                                <div className="flex items-center gap-1.5 mb-1">
                                    <TrendingUp size={14} className="opacity-80" />
                                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Owed to you</p>
                                </div>
                                <p className="text-xl font-black">{totalOwed.toFixed(2)}</p>
                            </div>
                            <div className="rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 p-4 text-white shadow-lg shadow-red-500/20">
                                <div className="flex items-center gap-1.5 mb-1">
                                    <TrendingDown size={14} className="opacity-80" />
                                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">You owe</p>
                                </div>
                                <p className="text-xl font-black">{totalOwe.toFixed(2)}</p>
                            </div>
                        </div>

                        {/* Balances List */}
                        {balances.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="mb-4 rounded-full bg-bg-card-alt p-5">
                                    <Users className="h-10 w-10 text-text-faint" />
                                </div>
                                <h3 className="text-lg font-bold text-text-body">All Settled Up!</h3>
                                <p className="text-sm text-text-muted mt-1 max-w-[240px]">
                                    You have no outstanding balances with anyone. 🎉
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-text-faint">
                                    {balances.length} {balances.length === 1 ? 'person' : 'people'}
                                </h3>

                                {balances.map((fb) => {
                                    const isExpanded = expandedId === fb.friendId;
                                    // Determine primary direction (net of all currencies)
                                    const totalNet = Object.values(fb.netByCurrency).reduce((s, v) => s + v, 0);
                                    const theyOweMe = totalNet > 0;

                                    return (
                                        <div
                                            key={fb.friendId}
                                            className="rounded-2xl border border-border-default bg-bg-card shadow-sm overflow-hidden transition-all"
                                        >
                                            {/* Main row */}
                                            <button
                                                onClick={() => { setExpandedId(isExpanded ? null : fb.friendId); }}
                                                className="w-full flex items-center justify-between p-4 text-left hover:bg-bg-hover transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={cn(
                                                        "flex h-11 w-11 items-center justify-center rounded-full font-bold text-sm",
                                                        theyOweMe
                                                            ? "bg-green-500/15 text-green-600 dark:text-green-400"
                                                            : "bg-red-500/15 text-red-600 dark:text-red-400"
                                                    )}>
                                                        {fb.displayName.slice(0, 2).toUpperCase() || '??'}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-text-heading">{fb.displayName}</p>
                                                        <p className={cn(
                                                            "text-[11px] font-semibold",
                                                            theyOweMe ? "text-green-600" : "text-red-600"
                                                        )}>
                                                            {theyOweMe ? 'owes you' : 'you owe'}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <div className="text-right">
                                                        {Object.entries(fb.netByCurrency).map(([currency, amount]) => (
                                                            <p key={currency} className={cn(
                                                                "text-sm font-black",
                                                                amount > 0 ? "text-green-600" : "text-red-600"
                                                            )}>
                                                                {amount > 0 ? '+' : ''}{formatCurrency(Math.abs(amount), currency)}
                                                            </p>
                                                        ))}
                                                    </div>
                                                    {isExpanded
                                                        ? <ChevronUp size={16} className="text-text-faint" />
                                                        : <ChevronDown size={16} className="text-text-faint" />
                                                    }
                                                </div>
                                            </button>

                                            {/* Breakdown */}
                                            {isExpanded && (
                                                <div className="border-t border-border-default bg-bg-card-alt px-4 py-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                                                    <p className="text-[9px] font-black uppercase tracking-widest text-text-faint mb-2">Breakdown</p>
                                                    {fb.breakdown.map((bd, idx) => (
                                                        <div key={idx} className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <span className={cn(
                                                                    "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase",
                                                                    bd.source === 'group'
                                                                        ? "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                                                                        : "bg-purple-500/15 text-purple-600 dark:text-purple-400"
                                                                )}>
                                                                    {bd.source}
                                                                </span>
                                                                <span className="text-xs text-text-body font-medium">{bd.label}</span>
                                                            </div>
                                                            <span className={cn(
                                                                "text-xs font-bold",
                                                                bd.amount > 0 ? "text-green-600" : "text-red-600"
                                                            )}>
                                                                {bd.amount > 0 ? '+' : ''}{formatCurrency(Math.abs(bd.amount), bd.currency)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
